<upload-repository>

    <div class="card bg-base-100 shadow-md hover:shadow-lg transition-shadow border-1 border-primary">
        <div class="card-body p-4">
            <!-- Header with Provider Icon -->
            <div class="flex items-start justify-between mb-3">
                <div class="flex items-center gap-3">
                    <!-- Height-locked, width follows the artwork. A fixed 48px square scaled
                         every logo to fit its WIDTH, which is right for the square marks and
                         ruinous for a wordmark: Moodle is 300x77, so it came out about ten
                         pixels tall and unreadable. `min-w-10` keeps a square mark in the
                         square well it wants, and `max-w-[104px]` stops a wide mark crowding
                         the title in a card only 276px across. Same treatment as the
                         migration console's `.mig-glyph`.

                         The wrapper this replaced carried `bg-neutral-focus`, a daisyUI 2
                         class that no longer resolves — the circular well it implied has been
                         invisible for some time, so nothing is lost by dropping the shape. -->
                    <div class="flex items-center justify-center h-10 min-w-10 max-w-[104px] shrink-0">
                        <!-- Ask whether a logo EXISTS, rather than whether the provider is
                             one of five hard-coded ids. The old gate duplicated knowledge
                             the lookup already has and drifted from it: an LMS connection
                             has a logo but was not on the list, so it fell through to the
                             generic folder emoji. Matches the idiom in push-modal.tag. -->
                        <img if="{tools?.getProviderLogoPath(props.integration.provider)}"
                             src="{tools?.getProviderLogoPath(props.integration.provider)}"
                             alt="{tools?.getProviderName(props.integration.provider)}"
                             class="h-full w-auto max-w-full object-contain" />
                        <!-- Fallback emoji, for a provider with no logo of its own. -->
                        <span if="{tools && !tools?.getProviderLogoPath(props.integration.provider)}" class="text-xl">
                            {tools?.getProviderIcon(props.integration.provider)}
                        </span>
                    </div>
                    <div>
                        <h3 class="card-title text-sm">{props.integration.displayName}</h3>
                        <p class="text-xs text-gray-500">{tools?.getProviderName(props.integration.provider)}</p>
                        <div class="flex items-center gap-2 mt-1">
                            <span class="badge badge-xs {tools?.getStatusBadgeClass(props.integration.connectionStatus)}">
                                {props.integration.connectionStatus === 'connected' ? locale?.t('connected') :
                                props.integration.connectionStatus === 'error' ? locale?.t('error_status') : locale?.t('disconnected')}
                            </span>
                            <!-- Show other users count for OAuth providers -->
                            <span if="{props.integration.otherUsersConnected > 0}" class="text-xs text-gray-500">
                                {locale?.t('uploadrepository_otherusers', {count: props.integration.otherUsersConnected})}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Connection Status & Actions -->
            <div class="flex flex-row gap-2">
                <div class="w-full text-center">
                    <!-- Connect button (when disconnected) -->
                    <button if="{props.integration.connectionStatus === 'disconnected'}"
                            class="btn btn-primary btn-sm uppercase rounded-lg px-8"
                            onclick="{() => connectOAuth(props.integration)}">
                        <i class="icon-metalark-link me-1"></i>
                        {locale?.t('connect')}
                    </button>

                    <!-- Retry button (when error) -->
                    <button if="{props.integration.connectionStatus === 'error'}"
                            class="btn btn-warning btn-sm uppercase rounded-lg px-8"
                            onclick="{() => connectOAuth(props.integration)}">
                        <i class="icon-metalark-refresh me-1"></i>
                        {locale?.t('retry')}
                    </button>

                    <!-- Browse Files button (when connected) -->
                    <div if="{props.integration.connectionStatus === 'connected'}"
                         class="{props.disablebrowse ? 'tooltip tooltip-top inline-block' : 'inline-block'}"
                         data-tip="{props.disablebrowse ? getDisabledMessage() : ''}">
                        <button class="btn btn-primary btn-sm uppercase rounded-lg px-8"
                                disabled="{props.disablebrowse}"
                                onclick="{() => browseFiles(props.integration)}">
                            <i class="icon-cloud1 text-lg"></i>
                            {locale?.t('browsefiles')}
                        </button>
                    </div>
                    <p if="{props.integration.connectionStatus === 'connected' && props.disablebrowse}"
                       class="text-xs text-gray-600 mt-2">
                        {getDisabledMessage()}
                    </p>
                </div>
            </div>
        </div>
    </div>

    <script>
        import { values } from '../services/values.js';
        import { getRepositoryService, getModalService } from '../services/service-loader';

        export default {
            values,

            // Mirrors upload-box.getDisabledMessage() so repository connectors
            // surface the same reason (quota / trial / grace / subscription)
            // when the Browse button is disabled — METALARK-307.
            getDisabledMessage() {
                if (!this.props.disablebrowse) return '';
                switch (this.props.disabledReason) {
                    case 'trial-expired':
                        return this.locale?.t('uploadbox_trialexpired') || 'Trial expired - upgrade to continue';
                    case 'grace-period':
                        return this.locale?.t('uploadbox_graceperiod') || 'Subscription in grace period - renew to upload';
                    case 'subscription-expired':
                        return this.locale?.t('uploadbox_subscriptionexpired') || 'Subscription expired - renew to continue';
                    case 'quota-exhausted':
                        return this.locale?.t('uploadbox_quotaexhausted') || 'Upload limit reached - no credits remaining';
                    default:
                        return this.locale?.t('uploadbox_uploadlimitreached') || 'Upload limit reached';
                }
            },

            async connectOAuth(integration) {
                try {
                    const repositoryService = await getRepositoryService();
                    const response = await repositoryService.getOAuthUrl(integration.repositoryId);

                    // Store repository ID and integration name for popup callback
                    sessionStorage.setItem('oauth_repository_id', integration.repositoryId);
                    sessionStorage.setItem('oauth_integration_name', integration.displayName);
                    sessionStorage.setItem('oauth_state', response.state);

                    console.log("Connect OAuth Response", response);

                    // Open OAuth flow in centered popup window
                    const width = 600;
                    const height = 700;
                    const left = window.screenX + (window.outerWidth - width) / 2;
                    const top = window.screenY + (window.outerHeight - height) / 2;

                    const popup = window.open(
                        response.authorizationUrl,
                        'oauth_popup',
                        `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
                    );

                    if (!popup) {
                        const modalService = await getModalService();
                        modalService.show(
                            this.locale?.t('uploadrepository_popupblocked') || 'Popup Blocked',
                            this.locale?.t('uploadrepository_popupblocked_message') || 'Please allow popups for this site and try again.'
                        );
                        return;
                    }

                    // Focus the popup
                    popup.focus();

                    const poll = setInterval(() => {
                        if (popup.closed) {
                            clearInterval(poll);
                            // Pass integration info to parent for retry logic (same as repositories.tag pattern)
                            if (this.props.onconnect) this.props.onconnect(integration);
                        }
                    }, 500);

                } catch (error) {
                    console.error('Failed to initiate OAuth:', error);
                    const modalService = await getModalService();
                    modalService.show(
                        this.locale?.t('uploadrepository_connectionfailed') || 'Connection Failed',
                        (this.locale?.t('uploadrepository_failedtoconnect') || 'Failed to connect:') + ' ' + (error.response?.data?.error?.message || error.message)
                    );
                }
            },

            browseFiles(integration) {
                if (this.props.onbrowse) this.props.onbrowse(integration);
            }

        }
    </script>
</upload-repository>